import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';

import { AuthService } from '@app/_modules/auth/services/auth.service';

import { Lesson, Profile, Student } from '@df/interfaces';
import { MAIN_DOMAIN, APP_PATH, GROUPS, LESSON_IN_WORK, DEFAULT_ACCESS_RIGHTS } from '@app/app.constants';
import { AccountService } from '@app/teacher/services/account.service';
import { AUTH_TOKEN } from '@app/_interceptors/auth.interceptor';
import { ResponseMain } from '@app/_interfaces/responses';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { LessonService } from '@teacher/services/lesson.service';
import { NotificationsService, PeopleService, EventsService } from '@app/core/services';
import { AccessService } from '@app/_services/access.service';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
    @ViewChild('openLessonMsg', { static: true }) openLessonMsg: TemplateRef<any>;

    profile: Profile;
    initials: string = '';
    main_domain: string = MAIN_DOMAIN;
    is_teacher: boolean = false;
    is_student: boolean = false;
    is_parent: boolean = false;
    is_teacher_helper: boolean = false;
    is_super_navigator: boolean = false;
    is_navigator: boolean = false;
    is_navigator_junior: boolean = false;
    is_metodist: boolean = false;
    APP_PATH = APP_PATH;
    GROUPS = GROUPS;
    lesson: Lesson;
    isShowButtonLesson: boolean = false;
    currentStudent: Student;
    groupInstance: any;
    student_amount = 0;
    showSideNav = false;
    dialogRef: any;
    userPhoto: string;
    countNewNotifications = 0;
    userNotifications: any = [];
    groupRights = DEFAULT_ACCESS_RIGHTS;
    isReportAccess: boolean = false;

    private stopCheckLessonInterval;

    links: Array<{ classLink: string; label: string; link: Array<string>; imgUrl: string }> = [
        {
            classLink: 'prepare',
            label: 'Подготовка',
            link: [APP_PATH.teacher.prepare],
            imgUrl: '/assets/images/menu_icons/preparation-new.png'
        },
        {
            classLink: 'teach',
            label: 'Проведение',
            link: [APP_PATH.teacher.teach],
            imgUrl: '/assets/images/menu_icons/conducting-new.png'
        },
        {
            classLink: 'analysis',
            label: 'Результаты',
            link: [`/${APP_PATH.results.main}`],
            imgUrl: '/assets/images/menu_icons/analysis-new.png'
        },
        {
            classLink: 'account',
            label: 'Банк',
            link: [APP_PATH.teacher.accounts],
            imgUrl: '/assets/images/menu_icons/bank-new.png'
        }
    ];
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private authService: AuthService,
        private accountService: AccountService,
        public dialog: MatDialog,
        private lessonService: LessonService,
        private notificationsService: NotificationsService,
        private peopleService: PeopleService,
        private router: Router,
        private eventsServices: EventsService,
        private accessService: AccessService
    ) {}

    ngOnInit(): void {
        const token = localStorage.getItem(AUTH_TOKEN);
        if (token) {
            this.accountService.getProfileData().subscribe(response => {
                if (response.data && response.data.user.username) {
                    this.profile = response.data;
                    this.userPhoto = this.main_domain + this.profile.avatar;
                    this.groupRights = this.accessService.checkAccess(this.profile.group.rights);
                    this.isReportAccess = this.groupRights.access_for_reports.is_access;

                    localStorage.setItem('profile', JSON.stringify(this.profile));
                    localStorage.setItem('userPhoto', this.userPhoto);
                    localStorage.setItem('groupRights', JSON.stringify(this.groupRights));

                    if (!this.profile.avatar) {
                        this.peopleService.getUserPhoto(this.profile.group.name).subscribe(resp => {
                            if (resp.success) {
                                this.userPhoto = this.main_domain + resp.data;
                                localStorage.setItem('userPhoto', this.userPhoto);
                            }
                        });
                    }

                    this.getCurrentGroupInstance();
                    this.getInitials();
                    this.checkCurrentUserNotifications();
                    this.whoCurrentUser();
                }
            });
        }
    }

    private whoCurrentUser(): void {
        if (this.profile.group.name === this.GROUPS.TEACHER) {
            this.is_teacher = true;
        }

        if (this.profile.group.name === this.GROUPS.PARENT) {
            this.is_parent = true;
        }

        if (this.profile.group.name === this.GROUPS.STUDENT) {
            this.is_student = true;
            this.checkLesson();

            // если пользователь заработал 'деньгу', то происходит подписка
            // на заработок и обновление инфы о балансе в шапке
            this.eventsServices.studentAmountSubject$.subscribe(value => {
                this.student_amount += value;
            });
        }

        if (this.profile.group.name === this.GROUPS.TEACHER_HELPER) {
            this.is_teacher_helper = true;
        }

        if (this.profile.group.name === this.GROUPS.SUPER_NAVIGATOR) {
            this.is_super_navigator = true;
        }

        if (this.profile.group.name === this.GROUPS.NAVIGATOR) {
            this.is_navigator = true;
        }

        if (this.profile.group.name === this.GROUPS.NAVIGATOR_JUNIOR) {
            this.is_navigator_junior = true;
        }

        if (this.profile.group.name === this.GROUPS.METODIST) {
            this.is_metodist = true;
        }
    }

    private checkCurrentUserNotifications(): void {
        this.notificationsService.getCurrentUserNotifications().subscribe(resp => {
            if (!resp.error) {
                this.countNewNotifications = resp.results.filter(it => !it.viewed).length;
                this.userNotifications = resp.results;
            }
        });
    }

    ngOnDestroy(): void {
        if (this.stopCheckLessonInterval) {
            clearInterval(this.stopCheckLessonInterval);
        }
        this.eventsServices.studentAmountSubject$.unsubscribe();
        this.destroy$.next(true);
    }

    logout(event: Event): void {
        event.preventDefault();
        this.authService.logout();
    }

    getInitials(): void {
        if (this.profile && this.profile.user) {
            const { first_name, last_name, username } = this.profile.user;
            if (first_name && last_name) {
                this.initials = `${first_name.charAt(0)}.${last_name.charAt(0)}`.toUpperCase();
                return;
            }
            this.initials = `${username.slice(0, 2)}`;
        }
    }

    private getCurrentGroupInstance(): void {
        this.accountService.getCurrentGroupInstance().subscribe((resp: ResponseMain) => {
            if (resp.data) {
                this.accountService.setCurrentGroupInstance = resp.data;
                this.groupInstance = resp.data;
                this.getStudentAmount();
            }
        });
    }

    private getStudentAmount(): void {
        if (this.groupInstance) {
            this.accountService.getStudentAccount(this.groupInstance.id).subscribe(resp => {
                this.student_amount = Math.round(resp.amount);
                this.currentStudent = resp.student;
            });
        }
    }

    openLessonDialog(): void {
        this.dialogRef = this.dialog.open(this.openLessonMsg, {
            width: '560px',
            height: '343px'
        });
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    openLesson(): void {
        this.closeDialog();
        let url: String = `/student/pupil/${this.currentStudent.id}`;
        if (this.lesson.smart_sprints.length || this.lesson.is_online) {
            url = `student/smart_sprint/pupil/${this.currentStudent.id}/${this.lesson.id}`;
        }

        if (this.lesson.is_habirint) {
            url = `student/coach/${this.currentStudent.student_class}`;
        }

        this.isShowButtonLesson = false;
        this.router.navigate([url]).then();
    }

    private checkLesson(): void {
        this.stopCheckLessonInterval = setInterval(() => {
            if (this.router.url.split('/')[1] === 'student') {
                this.isShowButtonLesson = false;
                return;
            }
            this.lessonService.checkStudentLesson().subscribe(
                resp => {
                    this.lesson = resp.data;
                    if (this.lesson.status === LESSON_IN_WORK) {
                        this.isShowButtonLesson = true;
                        return;
                    }
                    this.isShowButtonLesson = false;
                },
                // @ts-ignore-line
                error => {
                    clearInterval(this.stopCheckLessonInterval);
                }
            );
        }, 5000);
    }

    openNavUrl(url: String): void {
        this.showSideNav = false;
        this.router.navigate([url]).then();
    }

    canTournaments(): boolean {
        return !this.is_student || !this.is_parent;
    }

    showNotifications(): void {
        let url = ['/profile/notifications'];
        if (this.userNotifications.length === 1) {
            url = [`/profile/notifications/${this.userNotifications[0].id}`];
        }
        this.router.navigate(url).then();
    }
}
